import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Iter-276 PART 1 — Messenger-style Seller Chat FAB.
 *
 * A 56px circular bubble anchored bottom-right of the listing detail
 * page. Shows the seller's photo (top-level `listing.seller_photo` per
 * backend contract; initials fallback via `listing.seller_name`). A
 * gentle 6s bounce mimics Messenger — NOT a flashing indicator.
 *
 * Tap:
 *   - Anon viewer → sign-in gate (`/login?next=<current path>`), no panel.
 *   - Own listing OR `listing.allow_chat === false` → the whole
 *     component renders null (parent still mounts safely).
 *   - Logged-in non-owner → toggles an inline right-corner panel
 *     (~360×460) that never navigates away from the listing.
 *
 * Chat wiring reuses the existing endpoints:
 *   - `GET  /classifieds/chats`                     → find the existing conv for this listing
 *   - `POST /classifieds/chats {listing_id, message}` → first message creates the conv
 *   - `GET  /classifieds/chats/{id}/messages`       → 5s poll while the panel is open
 *   - `POST /classifieds/chats/{id}/messages {message}`
 *
 * The 5s poller is cleared on panel close AND on route unmount, so
 * navigating between listings with the panel open never leaves a
 * zombie interval behind.
 */

const LABEL_FADE_MS = 4800;
const POLL_MS = 5000;

function initials(name) {
  const s = (name || "").trim();
  if (!s) return "S";
  const parts = s.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase();
}

export default function SellerChatFab({ listing }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();

  const listingId = listing?.id;
  const ownerId = listing?.seller?.id || listing?.seller_id || listing?.user_id;
  const isOwner = !!(user?.id && ownerId && String(user.id) === String(ownerId));
  const chatDisabled = listing?.allow_chat === false;

  const [open, setOpen] = useState(false);
  const [labelVisible, setLabelVisible] = useState(true);
  const [convId, setConvId] = useState(null);
  const [draft, setDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const scrollerRef = useRef(null);

  // Fade the "Chat to me" label ~5s after mount.
  useEffect(() => {
    if (isOwner || chatDisabled) return;
    const t = setTimeout(() => setLabelVisible(false), LABEL_FADE_MS);
    return () => clearTimeout(t);
  }, [isOwner, chatDisabled]);

  // Look up an existing conversation for this listing once the user is
  // logged in and the panel opens. First send creates one if missing.
  useEffect(() => {
    if (!open || !user || !listingId || convId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await sharedApi.get("/classifieds/chats");
        const list = data?.conversations || data?.chats || [];
        const found = list.find((c) => String(c.listing_id) === String(listingId));
        if (!cancelled && found?.id) setConvId(found.id);
      } catch {
        /* no existing conv — first message will create one */
      }
    })();
    return () => { cancelled = true; };
  }, [open, user, listingId, convId]);

  const messages = useQuery({
    queryKey: ["chat-fab-messages", convId],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/chats/${convId}/messages`);
      return data?.messages || [];
    },
    enabled: !!convId && open,
    // Iter-276 hygiene: poll only while the panel is open. `enabled:false`
    // above already stops the automatic refetch when it closes;
    // `refetchInterval:false` when closed guards against a race where
    // the panel is closed mid-poll.
    refetchInterval: open ? POLL_MS : false,
    refetchIntervalInBackground: false,
  });

  // Explicitly clear the polling interval on route unmount (safety net —
  // react-query already handles the visible tab / enabled toggle).
  useEffect(() => {
    return () => {
      qc.cancelQueries({ queryKey: ["chat-fab-messages"] });
    };
  }, [loc.pathname, qc]);

  // Auto-scroll to the newest message when they arrive.
  useEffect(() => {
    if (!open || !scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.data, open]);

  const sendMut = useMutation({
    mutationFn: async (text) => {
      if (convId) {
        await sharedApi.post(`/classifieds/chats/${convId}/messages`, { message: text });
        return { convId };
      }
      // First message — create the conv AND deliver the message in one call.
      setCreating(true);
      try {
        const { data } = await sharedApi.post("/classifieds/chats", {
          listing_id: listingId,
          message: text,
        });
        const newId = data?.id || data?.conversation?.id;
        if (newId) setConvId(newId);
        return { convId: newId };
      } finally {
        setCreating(false);
      }
    },
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["chat-fab-messages"] });
    },
    onError: (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) toast.error(typeof detail === "string" ? detail : "Seller disabled chat on this ad");
      else if (status === 400) toast.error(typeof detail === "string" ? detail : "Cannot chat on your own ad");
      else toast.error(typeof detail === "string" ? detail : "Message failed to send");
    },
  });

  const onBubbleClick = () => {
    if (!user) {
      // Anon gate — same behaviour as Arrange-a-Viewing.
      nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`);
      return;
    }
    setOpen((o) => !o);
    setLabelVisible(false);
  };

  const onSend = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sendMut.isPending) return;
    sendMut.mutate(text);
  };

  // Hide the entire component when the viewer is the seller OR chat is
  // disabled on this ad.
  if (!listing || isOwner || chatDisabled) return null;

  const sellerPhoto = listing.seller_photo || null;
  const sellerName = listing.seller_name || listing.seller?.name || "Seller";
  const msgs = messages.data || [];

  return (
    <>
      <style>{`
        @keyframes allsale-chat-bounce {
          0%   { transform: translateY(0); }
          4%   { transform: translateY(-9px); }
          8.7% { transform: translateY(0); }
          13%  { transform: translateY(-4px); }
          17%  { transform: translateY(0); }
          100% { transform: translateY(0); }
        }
        .allsale-chat-bounce {
          animation: allsale-chat-bounce 6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .allsale-chat-bounce { animation: none; }
        }
      `}</style>

      {/* Bubble + "Chat to me" label */}
      {!open && (
        <div
          className="fixed z-40 right-4 flex items-center gap-2"
          style={{ bottom: "max(1rem, calc(env(safe-area-inset-bottom, 0) + 88px))" }}
        >
          {/* Iter-276 iter-102 fix: keep the label MOUNTED so the opacity
              transition can actually run. Toggle opacity + pointer-events
              instead of unmounting on state change. */}
          <span
            data-testid="seller-chat-fab-label"
            aria-hidden={!labelVisible}
            className="hidden md:inline-flex items-center rounded-full bg-white shadow-card border border-soft px-3 py-1.5 text-xs font-semibold text-slate-700 transition-opacity duration-500"
            style={{
              opacity: labelVisible ? 1 : 0,
              pointerEvents: labelVisible ? "auto" : "none",
            }}
          >
            Chat to me
          </span>
          <button
            type="button"
            data-testid="seller-chat-fab"
            aria-label={`Chat with ${sellerName}`}
            onClick={onBubbleClick}
            className="allsale-chat-bounce relative w-14 h-14 rounded-full bg-[color:var(--brand-blue)] text-white shadow-lg shadow-blue-500/30 hover:brightness-110 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-all"
          >
            {sellerPhoto ? (
              <img
                src={sellerPhoto}
                alt=""
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="w-full h-full grid place-items-center font-display font-black text-lg">
                {initials(sellerName)}
              </span>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-white grid place-items-center border-2 border-[color:var(--brand-blue)]">
              <MessageCircle className="w-3.5 h-3.5 text-[color:var(--brand-blue)]" />
            </span>
          </button>
        </div>
      )}

      {/* Inline panel */}
      {open && (
        <div
          data-testid="seller-chat-panel"
          className="fixed z-40 right-4 bg-white rounded-3xl shadow-2xl border border-soft flex flex-col overflow-hidden"
          style={{
            bottom: "max(1rem, calc(env(safe-area-inset-bottom, 0) + 88px))",
            width: "min(360px, calc(100vw - 2rem))",
            height: "min(460px, calc(100vh - 6rem))",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-soft bg-slate-50">
            {sellerPhoto ? (
              <img
                src={sellerPhoto}
                alt=""
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-9 h-9 rounded-full object-cover ring-2 ring-white"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[color:var(--brand-blue)] text-white grid place-items-center text-xs font-black">
                {initials(sellerName)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 text-sm truncate">{sellerName}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Typically replies within a day</div>
            </div>
            <button
              type="button"
              data-testid="seller-chat-close"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="text-slate-500 hover:text-slate-800 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Message list */}
          <div
            ref={scrollerRef}
            data-testid="seller-chat-messages"
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-white"
          >
            {messages.isLoading && convId ? (
              <div className="grid place-items-center h-full text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : msgs.length === 0 ? (
              <p className="text-center text-xs text-slate-400 pt-6">
                Say hi — the seller usually replies faster to short, friendly messages.
              </p>
            ) : (
              msgs.map((m) => (
                <div
                  key={m.id}
                  data-testid={`seller-chat-msg-${m.id}`}
                  className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.mine
                      ? "ml-auto bg-[color:var(--brand-blue)] text-white rounded-br-sm"
                      : "mr-auto bg-slate-100 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              ))
            )}
          </div>

          {/* Composer */}
          <form onSubmit={onSend} className="flex items-center gap-2 border-t border-soft px-3 py-2 bg-white">
            <input
              data-testid="seller-chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 rounded-full bg-slate-100 border-0 focus:ring-2 focus:ring-[color:var(--brand-blue)] px-4 py-2 text-sm"
              maxLength={2000}
              autoFocus
            />
            <button
              type="submit"
              data-testid="seller-chat-send"
              disabled={!draft.trim() || sendMut.isPending || creating}
              className="rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white w-9 h-9 grid place-items-center disabled:opacity-60 transition-colors"
              aria-label="Send message"
            >
              {(sendMut.isPending || creating) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
